import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import {
  SquaresFour, CurrencyDollar, ClipboardText, FilmSlate, FolderOpen,
  UsersThree, GearSix, SignOut, CaretDown, List, X,
} from "@phosphor-icons/react";
import { Brand } from "@/components/Brand";

const NAV = [
  { to: "/", label: "Overview", icon: SquaresFour, end: true, testid: "nav-overview" },
  { to: "/finance", label: "Finance", icon: CurrencyDollar, testid: "nav-finance" },
  { to: "/closeout", label: "Closeout", icon: ClipboardText, testid: "nav-closeout" },
  { to: "/production", label: "Production & Post", icon: FilmSlate, testid: "nav-production" },
  { to: "/documents", label: "Documents", icon: FolderOpen, testid: "nav-documents" },
  { to: "/people", label: "People & Credits", icon: UsersThree, testid: "nav-people" },
  { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
];

function ProjectSwitcher() {
  const { projects, current, setCurrentId } = useProjects();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="project-switcher"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-sm border border-[#2C2C2A] bg-[#1A1A18] px-3 py-1.5 text-sm text-[#F9F9F8] hover:bg-[#242422] transition-colors"
      >
        <span className="font-display">{current ? current.name : "Select project"}</span>
        <CaretDown size={14} className="text-[#A1A19D]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-1 w-64 rounded-sm border border-[#2C2C2A] bg-[#1A1A18] py-1 shadow-xl">
            {projects.map((p) => (
              <button
                key={p.id}
                data-testid={`project-option-${p.id}`}
                onClick={() => { setCurrentId(p.id); setOpen(false); }}
                className="block w-full px-3 py-2 text-left text-sm text-[#F9F9F8] hover:bg-[#242422] transition-colors"
              >
                <div className="font-medium">{p.name}</div>
                <div className="cm-label mt-0.5">{p.source_title}</div>
              </button>
            ))}
            {projects.length === 0 && (
              <div className="px-3 py-2 text-sm text-[#A1A19D]">No projects</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#121211] text-[#F9F9F8]">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#2C2C2A] bg-[#121211]/95 px-4 py-3 backdrop-blur md:px-8">
        <div className="flex items-center gap-4 md:gap-8">
          <button className="md:hidden text-[#A1A19D]" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
          <div className="hidden sm:block">
            <Brand logoHeight="h-9" mediaSize="text-[10px]" />
          </div>
          <div className="sm:hidden">
            <Brand logoHeight="h-7" mediaSize="text-[8px]" />
          </div>
          <div className="hidden md:block"><ProjectSwitcher /></div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <div className="text-sm text-[#F9F9F8]">{user?.name || user?.email}</div>
            <div className="cm-label">{user?.role}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={doLogout}
            title="Sign out"
            className="rounded-sm border border-[#2C2C2A] bg-[#1A1A18] p-2 text-[#A1A19D] hover:bg-[#242422] hover:text-[#F9F9F8] transition-colors"
          >
            <SignOut size={18} />
          </button>
        </div>
      </header>

      <div className="flex">
        <aside
          className={`${mobileOpen ? "block" : "hidden"} md:block fixed md:sticky top-[57px] z-20 h-[calc(100vh-57px)] w-60 shrink-0 border-r border-[#2C2C2A] bg-[#121211] px-3 py-6`}
        >
          <div className="md:hidden mb-4 px-2"><ProjectSwitcher /></div>
          <nav className="space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-[#1F1F1D] text-[#F9F9F8] border-l-2 border-[#F59E0B]"
                      : "text-[#A1A19D] hover:bg-[#1A1A18] hover:text-[#F9F9F8] border-l-2 border-transparent"
                  }`
                }
              >
                <item.icon size={18} weight="regular" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-8 md:px-10 md:py-10 cm-fade-in">{children}</main>
      </div>
    </div>
  );
}
